import React from "react";
import { ButtonVariant, CoButton } from "components/CoButton";
import { SNAPSHOT_CREDITS_PER_MONTH_FREE_USER } from "constants/config";
import { SubscriptionPlan } from "enums/subscription-plans";
import { SubscriptionPlanIcon } from "features/Pricing/utils";
import useCredits from "features/User/hooks/useCredits";
import pluralize from "helpers/pluralize";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { CreditedFeature } from "store/slices/subscription";
import { usePremiumFeatureModal } from "../PremiumFeatureModal";

type SpendCreditOnSnapshotProps = {
  preventEventDefault?: boolean;
  stopEventPropagation?: boolean;
  onUseCreditClick: () => void;
  containerClassName?: string;
};

/**
 * @component SpendCreditOnSnapshot
 * @description Prompt the user to opt-in to using a credit for a snapshot or upgrade to premium. If other features
 * require a similar overlay, the component can bew renamed to SpendCreditOnFeature with a feature passed in as a prop.
 */
function SpendCreditOnSnapshot({
  preventEventDefault,
  stopEventPropagation,
  containerClassName = "",
  onUseCreditClick,
}: SpendCreditOnSnapshotProps) {
  // If this overlay is required by other features, the feature variable can be promoted to a prop
  const feature = CreditedFeature.STUDY_SNAPSHOT;

  const [pageLabels, featureLabels, premiumDescription] = useLabels(
    "use-credits",
    `credits.features.${feature}`,
    "subscription-overlay.premium-description"
  );

  const { openPremiumFeatureModal } = usePremiumFeatureModal();

  const onClickUpgradeToPremium = (e: React.MouseEvent) => {
    preventEventDefault && e.preventDefault();
    stopEventPropagation && e.stopPropagation();
    openPremiumFeatureModal({
      feature,
      actionSource: MixpanelActionSource.StudySnapshot,
    });
  };

  const MAX_CREDITS = SNAPSHOT_CREDITS_PER_MONTH_FREE_USER;

  const { isUsageSet, creditRefreshDate, featureToCreditsRemainingMap } =
    useCredits();

  const creditsRemaining = featureToCreditsRemainingMap[feature];

  if (!isUsageSet) {
    return null;
  }
  const title = pluralize(pageLabels["title"], {
    creditsRemaining,
    featureSingular: featureLabels["title"],
    featurePlural: featureLabels["plural"],
  });

  const featureDescription = pluralize(pageLabels["description"], {
    creditsRemaining,
    featureSingular: featureLabels["title"],
    featurePlural: featureLabels["plural"],
    MAX_CREDITS: MAX_CREDITS,
    RESET_DATE: creditRefreshDate,
  });

  return (
    <div className={containerClassName}>
      <h1 className="mb-2 base-md">{title}</h1>
      <p className="sm-normal">
        <span dangerouslySetInnerHTML={{ __html: featureDescription }}></span>{" "}
        {premiumDescription}
      </p>
      <div className="flex gap-2 mt-2.5 flex-wrap">
        <CoButton
          icon={
            <SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} size="sm" />
          }
          label={pageLabels["upgrade-to-premium"]}
          onClick={onClickUpgradeToPremium}
          size="sm"
        />
        <CoButton
          label={featureLabels["generate-cta"]}
          onClick={onUseCreditClick}
          size="sm"
          variant={ButtonVariant.Tertiary}
        />
      </div>
    </div>
  );
}

export default SpendCreditOnSnapshot;
